import { useContext, useMemo } from "react";
import { ENTITY_LABELS } from "../../../utils/labelMapping";
import { DiscoveryContext } from "../discovery-context";
import { tab } from "../BrandScore/BrandScore";
import { generateCombination } from "../../../utils/helpers";
import { resolvePersonaScope, getPersonasForScope } from "./PersonaScopeResolver";


export function generateComponentKeys({
  dashboardTab,
  category = null,
  allCategories,
  allLLMs,
  allMarkets,
  allPersonas,
  discoveryCtx,
}) {
  const extractPersonaTitles = (personas = []) =>
    personas.map(p => (typeof p === "string" ? p : p?.title)).filter(Boolean);
  const Personas = extractPersonaTitles(discoveryCtx.persona);
  const primaryMarket = discoveryCtx.market;
  const primaryLLM = discoveryCtx.primary_llm;
  const primaryPersona = Personas[0];
  const discoveryType = discoveryCtx.type || "brand";
  const entity = discoveryCtx.entity;
  const competitors = discoveryCtx?.competitors?.[category] || [];
  
  // Resolve persona scope based on settings
  const personaSetting = discoveryCtx?.state?.personaSettings;
  const primaryCategory = discoveryCtx?.category[0];
  
  const personaScopeMap = resolvePersonaScope({
    categories: Array.isArray(allCategories) ? allCategories : [allCategories],
    markets: Array.isArray(allMarkets) ? allMarkets : [allMarkets],
    personas: Array.isArray(allPersonas) ? allPersonas : [allPersonas],
    primaryCategory,
    primaryMarket,
    primaryPersona,
    personaSetting,
  });

  const isCategoryTab = !!category;
  const keys = [];
  const componentDefs = getDashboardComponents(dashboardTab, isCategoryTab, discoveryType);
  

  // Build all possible combinations once
  const filter = {
    llm: allLLMs,
    category: allCategories,
    market: allMarkets,
    persona: allPersonas,
  };

  // Get unique dependencies across all components to generate base combinations
  const allDependencies = [...new Set(componentDefs.flatMap(c => c.dependencies))];
  const baseCombinationData = allDependencies.map((dep) => filter[dep]);
  const baseCombinations = generateCombination(baseCombinationData, allDependencies);


  // Iteration order: combination-first → metrics-second
  baseCombinations.forEach((baseComb) => {
    componentDefs.forEach((compDef) => {
      const { id, dependencies, isSpecialCase } = compDef;

      // Extract only the dependencies needed for this component
      const relevantComb = {};
      dependencies.forEach((dep) => {
        if (baseComb[dep] !== undefined) {
          relevantComb[dep] = baseComb[dep];
        }
      });

      // Validate persona scope if persona is in this combination
      if (relevantComb.persona) {
        const validPersonas = getPersonasForScope({
          scopeMap: personaScopeMap,
          category: relevantComb.category || category || primaryCategory,
          market: relevantComb.market || primaryMarket,
          allPersonas: Array.isArray(allPersonas) ? allPersonas : [allPersonas],
        });
        
        // Skip this combination if persona is not valid for this category-market pair
        if (!validPersonas.includes(relevantComb.persona)) {
          return;
        }
      }

      if (!isSpecialCase) {
        const uniqueKey = generateDashboardUniqueKey({
          dashboardTab,
          id,
          selectedLLM: relevantComb.llm,
          selectedCategory: relevantComb.category,
          selectedMarket: relevantComb.market,
          selectedPersona: relevantComb.persona,
          category,
          isCategoryTab,
          discoveryType,
        });

        keys.push({
          uniqueKey,
          filters: {
            ...buildDashboardFilters({
              id,
              dashboardTab,
              selectedLLM: relevantComb.llm,
              selectedCategory: relevantComb.category,
              selectedMarket: relevantComb.market,
              selectedPersona: relevantComb.persona,
              category,
              isCategoryTab,
              discoveryType,
              primaryMarket,
              primaryLLM,
              primaryPersona,
              discoveryCtx,
            }),
            uniqueKey,
          },
        });
      } else {
        // Handle special case with competitors
        let competitorsList;

        if (isCategoryTab) {
          competitorsList = [{ name: entity }, ...competitors];
        } else if (dashboardTab === "nev_competitiveoverview") {
          const categoryFromCombination = relevantComb.category;
          const competitorsForCategory = discoveryCtx?.competitors?.[categoryFromCombination] || [];
          competitorsList = [{ name: entity }, ...competitorsForCategory];
        } else {
          competitorsList = [{ name: entity }];
        }

        competitorsList.forEach((competitor, competitorIndex) => {
          const uniqueKey = generateDashboardUniqueKey({
            dashboardTab,
            id,
            selectedLLM: relevantComb.llm,
            selectedCategory: relevantComb.category,
            selectedMarket: relevantComb.market,
            selectedPersona: relevantComb.persona,
            category,
            isCategoryTab,
            discoveryType,
            competitor: competitorIndex,
          });

          keys.push({
            uniqueKey,
            filters: {
              ...buildDashboardFilters({
                id,
                dashboardTab,
                selectedLLM: relevantComb.llm,
                selectedCategory: relevantComb.category,
                selectedMarket: relevantComb.market,
                selectedPersona: relevantComb.persona,
                category,
                isCategoryTab,
                discoveryType,
                primaryMarket,
                primaryLLM,
                primaryPersona,
                discoveryCtx,
                competitor: competitorIndex,
                competitorName: competitor.name,
              }),
              uniqueKey,
            },
          });
        });
      }
    });
  });

  // Deduplicate based on uniqueKey (keep first occurrence)
  const seenKeys = new Set();
  const deduplicatedKeys = keys.filter((item) => {
    if (seenKeys.has(item.uniqueKey)) {
      return false;
    }
    seenKeys.add(item.uniqueKey);
    return true;
  });

  return deduplicatedKeys;
}

export function useDashboardComponents(dashboardTab, category = null) {
    const discoveryCtx = useContext(DiscoveryContext);

    const extractPersonaTitles = (personas = []) =>
        personas.map(p => (typeof p === "string" ? p : p?.title)).filter(Boolean);

    // Get arrays of all available values
    const allLLMs = Array.isArray(discoveryCtx.llm) ? discoveryCtx.llm : [discoveryCtx.llm];
    const allCategories = Array.isArray(discoveryCtx.category) ? discoveryCtx.category : [discoveryCtx.category];
    const allMarkets = Array.isArray(discoveryCtx.markets) ? discoveryCtx.markets : [discoveryCtx.markets];
    const allPersonas = extractPersonaTitles(discoveryCtx.persona);
    const selectedSection = discoveryCtx.state.selectedSection || {};

    // Generate all component keys for this dashboard
  const componentKeys = useMemo(() => {
    return generateComponentKeys({
      dashboardTab,
      category,
      allCategories,
      allLLMs,
      allMarkets,
      allPersonas,
      discoveryCtx,
    });
  }, [
    dashboardTab,
    allLLMs,
    allCategories,
    allMarkets,
    allPersonas,
    category,
    discoveryCtx.type,
    discoveryCtx.entity,
    discoveryCtx.market,
    discoveryCtx.primary_llm,
    discoveryCtx?.competitors,
  ]);

  // Check selection state
  const allSelected = componentKeys.every((comp) => selectedSection[comp.uniqueKey]);
  const someSelected = componentKeys.some((comp) => selectedSection[comp.uniqueKey]);

  return {
    componentKeys,
    allSelected,
    someSelected: someSelected && !allSelected,
    componentCount: componentKeys.length,
  };
}


 function getDashboardComponents(dashboardTab, isCategoryTab, discoveryType) {
    if (isCategoryTab) {
        return [
            { 
                id: "BrandInsights",
                dependencies: ["llm"],
            },
            { 
                id: "indexscore",
                dependencies: ["market", "persona"],
            },
            { 
                id: "competitor",
                dependencies: ["llm", "market", "persona"],
            },
            { 
                id: "Reach_data",
                dependencies: ["llm", "market", "persona"],
                isSpecialCase: true,
            },
            { 
                id: "sentiment_data",
                dependencies: ["llm", "market", "persona"],
                isSpecialCase: true,
            },
            { 
                id: "sentiment_driver",
                dependencies: ["llm", "market", "persona"],
                isSpecialCase: true,
            },
            { 
                id: "positiveNegativeAttributes",
                dependencies: ["llm", "persona"],
            },
            { 
                id: "Brand_Piller",
                dependencies: ["llm", "persona"],
            },
            { 
                id: "purchase_criteria",
                dependencies: ["llm", "persona"],
            },
            { 
                id: "sources",
                dependencies: ["llm", "market", "persona"],
            },
            { 
                id: "realtimeActions",
                dependencies: ["llm", "market", "persona"],
            },
        ];
    }

    const dashboardMap = {
        [tab.brandOverview]: [
            { 
                id: "Brand_Summary",
                dependencies: ["market", "persona"],
            },
            { 
                id: "Sentiment_Drivers",
                dependencies: ["llm", "category", "market", "persona"],
            },
            { 
                id: "Attributes_Overview",
                dependencies: ["category", "persona"],
            },
            { 
                id: "Sources_Overview",
                dependencies: ["category", "market", "persona"],
            },
            { 
                id: "FocusArea_Overview",
                dependencies: ["category", "market", "persona"],
            },
        ],
        [tab.competitiveOverview]: [
            { 
                id: "Competitor_Summary",
                dependencies: ["category", "persona", "market"],
            },
            { 
                id: "Sentiment_Drivers",
                dependencies: ["llm", "category", "persona", "market"],
                isSpecialCase: true, 
            },
            { 
                id: "Attributes_Overview",
                dependencies: ["llm", "category", "persona"],
            },
            { 
                id: "TaxonomyAndCoreAttributes",
                dependencies: ["llm", "category", "persona"],
            },
            { 
                id: "purchase_criteria",
                dependencies: ["llm", "category", "persona"],
            },
        ],
        [tab.marketOverview]: [
            { 
                id: "Market_Summary",
                dependencies: ["category", "persona"],
            },
            { 
                id: "Sentiment_Drivers",
                dependencies: ["llm", "category", "market", "persona"],
            },
        ],
        [tab.personaOverview]: [
            { 
                id: "Persona_Summary",
                dependencies: ["category", "market"],
            },
            { 
                id: "Sentiment_Drivers",
                dependencies: ["llm", "category", "market", "persona"],
            },
        ],
    };

    return dashboardMap[dashboardTab] || [];
}


 function generateDashboardUniqueKey({
    dashboardTab,
    id,
    selectedLLM,
    selectedCategory,
    selectedMarket,
    selectedPersona,
    category,
    isCategoryTab,
    discoveryType,
    competitor,
}) {
    let actualId = id;
    if (id === "Brand_Piller" && discoveryType === "product") {
        actualId = "Product_Dimension";
    }

    // Brand Overview
    if (dashboardTab === "nev_brandoverview") {
        if (id === "Brand_Summary") {
            return `${dashboardTab}_${id}_${selectedMarket}_${selectedPersona}`;
        }
        if (id === "Sentiment_Drivers") {
            return `${dashboardTab}_${id}_${selectedLLM ?? ""}_${selectedCategory ?? ""}_${selectedMarket ?? ""}_${selectedPersona ?? ""}`;
        }
        if (id === "Attributes_Overview") {
            return `${dashboardTab}_${id}_${selectedCategory}_${selectedPersona}`;
        }
        if (id === "Sources_Overview") {
            return `${dashboardTab}_${id}_${selectedCategory}_${selectedMarket}_${selectedPersona}`;
        }
        if (id === "FocusArea_Overview") {
            return `${dashboardTab}_${id}_${selectedCategory}_${selectedMarket}_${selectedPersona}`;
        }
    }

    // Competitive Overview
    if (dashboardTab === "nev_competitiveoverview") {
        if (id === "Competitor_Summary") {
            return `${dashboardTab}_${id}_${selectedCategory}_${selectedMarket}_${selectedPersona}`;
        }
        if (id === "Sentiment_Drivers") {
            return `${dashboardTab}_${id}_${selectedLLM}_${selectedCategory}_${selectedMarket}_${selectedPersona}_${competitor ?? 0}`;
        }
        if (id === "Attributes_Overview" || id === "TaxonomyAndCoreAttributes" || id === "purchase_criteria") {
            return `${dashboardTab}_${id}_${selectedLLM ?? ""}_${selectedCategory}_${selectedPersona}`;
        }
    }

    // Market Overview
    if (dashboardTab === "nev_marketoverview") {
        if (id === "Market_Summary") {
            return `${dashboardTab}_${id}_${selectedCategory}_${selectedPersona}`;
        }
        if (id === "Sentiment_Drivers") {
            return `${dashboardTab}_${id}_${selectedLLM ?? ""}_${selectedCategory ?? ""}_${selectedMarket ?? ""}_${selectedPersona ?? ""}`;
        }
    }

    // Persona Overview
    if (dashboardTab === "nev_personaoverview") {
        if (id === "Persona_Summary") {
            return `${dashboardTab}_${id}_${selectedCategory}_${selectedMarket}`;
        }
        if (id === "Sentiment_Drivers") {
            return `${dashboardTab}_${id}_${selectedLLM ?? ""}_${selectedCategory ?? ""}_${selectedMarket ?? ""}_${selectedPersona ?? ""}`;
        }
    }

    // Category Tabs
    if (isCategoryTab) {
        if (id === "BrandInsights") {
            return `${dashboardTab}_${actualId}_${selectedLLM}`;
        }
        if (id === "indexscore") {
            return `${dashboardTab}_${actualId}_${selectedMarket ?? ""}_${selectedPersona ?? ""}`;
        }
        if (id === "competitor") {
            return `${dashboardTab}_${actualId}_${selectedLLM ?? ""}_${selectedMarket}_${selectedPersona}`;
        }
        if (["Reach_data", "sentiment_data", "sentiment_driver"].includes(id)) {
            return `${dashboardTab}_${actualId}_${selectedLLM ?? ""}_${selectedMarket ?? ""}_${selectedPersona ?? ""}_${competitor ?? 0}`;
        }
        if (["positiveNegativeAttributes", "Brand_Piller", "Product_Dimension", "purchase_criteria"].includes(id)) {
            return `${dashboardTab}_${actualId}_${selectedLLM ?? ""}_${selectedPersona}`;
        }
        if (["sources", "realtimeActions"].includes(id)) {
            return `${dashboardTab}_${actualId}_${selectedLLM ?? ""}_${selectedMarket ?? ""}_${selectedPersona ?? ""}`;
        }
    }

    return `${dashboardTab}_${actualId}`;
}


 function buildDashboardFilters({
    id,
    dashboardTab,
    selectedLLM,
    selectedCategory,
    selectedMarket,
    selectedPersona,
    category,
    isCategoryTab,
    discoveryType,
    primaryMarket,
    primaryLLM,
    primaryPersona,
    discoveryCtx,
    competitor,
    competitorName,
}) {
    const getTitle = (id) => {
        const titleMap = {
            Brand_Summary: discoveryType === "brand" ? `${ENTITY_LABELS.brand} Summary` : `${ENTITY_LABELS.product} Summary`,
            Sentiment_Drivers: "Sentiment Driver",
            Attributes_Overview: "Positive and Negative Attributes",
            Sources_Overview: "Sources Overview",
            FocusArea_Overview: "Focus Area",
            Competitor_Summary: "Competitor Summary",
            TaxonomyAndCoreAttributes: discoveryType === "brand" ? ENTITY_LABELS.brandPillar : ENTITY_LABELS.productDimension,
            purchase_criteria: ENTITY_LABELS.purchaseCriteria,
            Market_Summary: "Market Summary",
            Persona_Summary: "Persona Summary",
            BrandInsights: `${discoveryType === "brand" ? `${ENTITY_LABELS.brand}` : `${ENTITY_LABELS.product}`} Insights`,
            indexscore: `${discoveryType === "brand" ? `${ENTITY_LABELS.brand}` : `${ENTITY_LABELS.product}`} Index Score`,
            competitor: discoveryType === "category" ? "Products" : "Competitor Analysis",
            Reach_data: `${ENTITY_LABELS.rank} Data`,
            sentiment_data: "Sentiment Data",
            sentiment_driver: "Sentiment Driver",
            positiveNegativeAttributes: "Positive and Negative Attributes",
            Brand_Piller: ENTITY_LABELS.brandPillar,
            Product_Dimension: ENTITY_LABELS.productDimension,
            sources: "Sources",
            realtimeActions: "Focus Area",
        };
        return discoveryCtx?.mapMetricTitle?.(titleMap[id]) ?? titleMap[id] ?? id;
    };

    const filters = {
        id,
        title: getTitle(id),
        tab: dashboardTab,
    };

    // Add specific filters based on dashboard and component
    if (dashboardTab === "nev_brandoverview") {
        if (id === "Brand_Summary") {
            filters.llm = null;
            filters.category = null;
            filters.market = selectedMarket;
            filters.persona = selectedPersona;
        } else if (id === "Sentiment_Drivers") {
            filters.llm = selectedLLM;
            filters.category = selectedCategory;
            filters.market = selectedMarket;
            filters.persona = selectedPersona;
        } else if (id === "Attributes_Overview") {
            filters.llm = null;
            filters.category = selectedCategory;
            filters.market = primaryMarket;
            filters.persona = selectedPersona;
        } else if (id === "Sources_Overview" || id === "FocusArea_Overview") {
            filters.llm = null;
            filters.category = selectedCategory;
            filters.market = selectedMarket;
            filters.persona = selectedPersona;
        }
    } else if (dashboardTab === "nev_competitiveoverview") {
        if (id === "Competitor_Summary") {
            filters.llm = null;
            filters.category = selectedCategory;
            filters.market = selectedMarket;
            filters.persona = selectedPersona;
        } else if (id === "Sentiment_Drivers") {
            filters.llm = selectedLLM;
            filters.category = selectedCategory;
            filters.market = selectedMarket;
            filters.persona = selectedPersona;
            filters.competitor = competitor;
            filters.competitorName = competitorName;
        } else {
            filters.llm = selectedLLM;
            filters.category = selectedCategory;
            filters.market = primaryMarket;
            filters.persona = selectedPersona;
        }
    } else if (dashboardTab === "nev_marketoverview") {
        if (id === "Market_Summary") {
            filters.llm = null;
            filters.category = selectedCategory;
            filters.market = null;
            filters.persona = selectedPersona;
        } else {
            filters.llm = selectedLLM;
            filters.category = selectedCategory;
            filters.market = selectedMarket;
            filters.persona = selectedPersona;
        }
    } else if (dashboardTab === "nev_personaoverview") {
        if (id === "Persona_Summary") {
            filters.llm = null;
            filters.category = selectedCategory;
            filters.market = selectedMarket;
            filters.persona = null;
        } else {
            filters.llm = selectedLLM;
            filters.category = selectedCategory;
            filters.market = selectedMarket;
            filters.persona = selectedPersona;
        }
    } else if (isCategoryTab) {
        filters.category = category;
        filters.isCategoryTab = true;

        if (id === "BrandInsights") {
            filters.llm = selectedLLM;
        } else if (id === "indexscore") {
            filters.market = selectedMarket;
            filters.llm = null;
            filters.persona = selectedPersona;
        } else if (["Reach_data", "sentiment_data", "sentiment_driver"].includes(id)) {
            filters.llm = selectedLLM;
            filters.market = selectedMarket;
            filters.persona = selectedPersona;
            filters.competitor = competitor;
            filters.competitorName = competitorName;
        } else if (["positiveNegativeAttributes", "Brand_Piller", "Product_Dimension", "purchase_criteria"].includes(id)) {
            filters.llm = selectedLLM;
            filters.persona = selectedPersona;
        } else if (["sources", "realtimeActions","competitor"].includes(id)) {
            filters.llm = selectedLLM;
            filters.persona = selectedPersona;
            filters.market = selectedMarket;
        } else {
            filters.market = selectedMarket;
            filters.persona = selectedPersona;
        }
    }

    return filters;
}

export function getDashboardDependencies(discoveryCtx) {
  const selectedSection = discoveryCtx.state.selectedSection || {};
  const allLLMs = Array.isArray(discoveryCtx.llm) ? discoveryCtx.llm : [discoveryCtx.llm];
  const allCategories = Array.isArray(discoveryCtx.category) ? discoveryCtx.category : [discoveryCtx.category];
  const allMarkets = Array.isArray(discoveryCtx.markets) ? discoveryCtx.markets : [discoveryCtx.markets];
  const extractPersonaTitles = (personas = []) =>
    personas.map(p => (typeof p === "string" ? p : p?.title)).filter(Boolean);
  const allPersonas = extractPersonaTitles(discoveryCtx.persona);

  const selectedCombinations = Object.values(selectedSection);
  
  if (selectedCombinations.length === 0) {
    return null;
  }

  const dashboardGroups = {};
  selectedCombinations.forEach(combo => {
    const dashboardId = combo.tab;
    if (!dashboardId) return;
    
    if (!dashboardGroups[dashboardId]) {
      dashboardGroups[dashboardId] = [];
    }
    dashboardGroups[dashboardId].push(combo);
  });

  const selectedDashboardIds = Object.keys(dashboardGroups);
  
  if (selectedDashboardIds.length === 0) {
    return null;
  }

  const isDashboardLevelSelection = selectedDashboardIds.every(dashboardId => {
    const dashboardCombos = dashboardGroups[dashboardId];
    
    const isCategoryTab = !['nev_brandoverview', 'nev_competitiveoverview', 'nev_marketoverview', 'nev_personaoverview'].includes(dashboardId);
    const category = isCategoryTab ? dashboardCombos[0]?.category : null;
    
    const expectedKeys = generateComponentKeys({
      dashboardTab: dashboardId,
      category,
      allCategories,
      allLLMs,
      allMarkets,
      allPersonas,
      discoveryCtx,
    });

    const allExpectedSelected = expectedKeys.every(expected => 
      selectedSection[expected.uniqueKey]
    );

    return allExpectedSelected;
  });

  if (!isDashboardLevelSelection) {
    return null;
  }

  const mergedCategories = new Set();
  const mergedLLMs = new Set();
  const mergedMarkets = new Set();
  const mergedPersonas = new Set();

  selectedDashboardIds.forEach(dashboardId => {
    const dashboardCombos = dashboardGroups[dashboardId];
    
    dashboardCombos.forEach(combo => {
      if (combo.category) mergedCategories.add(combo.category);
      if (combo.llm) mergedLLMs.add(combo.llm);
      if (combo.market) mergedMarkets.add(combo.market);
      if (combo.persona) mergedPersonas.add(combo.persona);
    });
  });

  return {
    isDashboardSelection: true,
    dashboardIds: selectedDashboardIds,
    categories: mergedCategories.size > 0 
      ? Array.from(mergedCategories) 
      : [allCategories[0]], 
    llms: mergedLLMs.size > 0 
      ? Array.from(mergedLLMs) 
      : [discoveryCtx.primary_llm], 
    markets: mergedMarkets.size > 0 
      ? Array.from(mergedMarkets) 
      : [discoveryCtx.market], 
    personas: mergedPersonas.size > 0 
      ? Array.from(mergedPersonas) 
      : [allPersonas[0]], 
    dashboards: selectedDashboardIds,
  };
}